import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import TabelaApontamentosMontagem from "./components/TabelaApontamentosMontagem";
import blogFetch from "../../axios/config";
import { Apontamento } from "./components/Types";
import TabelaApontamentosGeral from "./components/TabelaApontamentosGeral";

const CENTROS_TRABALHO = [
    '01CORTE', '01HIDCOR', '03SERIG', '05EMPOLV', '06CURVAD', '09IOX_E',
    '09IOX_S', '12PRDACO', '13PRE_IB', '14MONTAG', '15EMBOL',
    '16ACV', '18INSFIN', '57ENGENH', '57LABORA'
];

function toLocalDateStr(dateString: string): string {
    const date = new Date(dateString);
    date.setHours(date.getHours() - 3);
    return date.toISOString().split('T')[0];
}

function ResumoCard({ label, valor, colorClass }: { label: string; valor: number; colorClass: string }): JSX.Element {
    return (
        <div className={`card border-0 text-white ${colorClass} text-center px-3 py-1`} style={{ minWidth: 90 }}>
            <div className="fw-bold lh-1" style={{ fontSize: "1.4rem" }}>{valor}</div>
            <div className="opacity-75" style={{ fontSize: "0.7rem" }}>{label}</div>
        </div>
    );
}

export default function HistoricoApontamentos(): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawSector = searchParams.get('sector') ?? '';
    const centroTrabalhoSelect = CENTROS_TRABALHO.includes(rawSector) ? rawSector : '';
    const [dataFiltro, setDataFiltro] = useState('');
    const [apontamentos, setApontamentos] = useState<Apontamento[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleApontamentos = async () => {
        setApontamentos([]);
        if (!centroTrabalhoSelect) return;
        setIsLoading(true);
        try {
            const { data } = await blogFetch.get('HistoricoApontamento/GetHistoricoApontamentos', {
                params: { CentroTrabalho: centroTrabalhoSelect }
            });
            if (data && data.length > 0) {
                setApontamentos(data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleApontamentos();
    }, [centroTrabalhoSelect]);

    const apontamentosFiltrados = useMemo(() => {
        if (!dataFiltro) return apontamentos;
        return apontamentos.filter(ap => toLocalDateStr(ap._ts_Creat) === dataFiltro);
    }, [apontamentos, dataFiltro]);

    const stats = useMemo(() => ({
        total: apontamentosFiltrados.length,
        ordensUnicas: new Set(apontamentosFiltrados.map(ap => ap.ordem_Fabricacao)).size,
        success: apontamentosFiltrados.filter(ap => ap.integration_Status === 'SUCCESS').length,
        error: apontamentosFiltrados.filter(ap => ap.integration_Status === 'ERROR').length,
        naFila: apontamentosFiltrados.filter(ap => ap.integration_Status === 'NA FILA').length,
    }), [apontamentosFiltrados]);

    const isMontagem = centroTrabalhoSelect === '14MONTAG';
    const showTable = centroTrabalhoSelect !== '';

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body">
                    <h4 className="fw-bold text-primary mb-3">Historico de Apontamentos</h4>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div>
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Centro de Trabalho
                                    </label>
                                    <select
                                        className="form-select form-select-sm shadow-sm"
                                        value={centroTrabalhoSelect}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSearchParams(prev => {
                                                if (value) prev.set('sector', value);
                                                else prev.delete('sector');
                                                return prev;
                                            });
                                            setDataFiltro('');
                                        }}
                                    >
                                        <option value="">Escolha um Centro</option>
                                        {CENTROS_TRABALHO.map((centro) => (
                                            <option key={centro} value={centro}>{centro}</option>
                                        ))}
                                    </select>
                                </div>

                                {showTable && (
                                    <div>
                                        <label className="form-label small fw-semibold text-muted mb-1">
                                            Filtrar por Dia
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm shadow-sm"
                                            value={dataFiltro}
                                            onChange={(e) => setDataFiltro(e.target.value)}
                                        />
                                    </div>
                                )}

                                {dataFiltro && (
                                    <button
                                        className="btn btn-sm btn-outline-secondary align-self-end"
                                        onClick={() => setDataFiltro('')}
                                    >
                                        Limpar filtro
                                    </button>
                                )}

                                {showTable && !isLoading && apontamentos.length > 0 && (
                                    <div className="ms-auto d-flex flex-wrap gap-2 align-items-end">
                                        <ResumoCard label="Apontamentos" valor={stats.total} colorClass="bg-primary" />
                                        <ResumoCard label="Ordens Únicas" valor={stats.ordensUnicas} colorClass="bg-secondary" />
                                        <ResumoCard label="Sucesso" valor={stats.success} colorClass="bg-success" />
                                        <ResumoCard label="Erro" valor={stats.error} colorClass="bg-danger" />
                                        <ResumoCard label="Na Fila" valor={stats.naFila} colorClass="bg-warning text-dark" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {isLoading && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="text-muted mt-2">Carregando apontamentos...</p>
                        </div>
                    )}

                    {!isLoading && isMontagem && (
                        <TabelaApontamentosMontagem apontamentos={apontamentosFiltrados} />
                    )}

                    {!isLoading && showTable && !isMontagem && (
                        <TabelaApontamentosGeral apontamentos={apontamentosFiltrados} />
                    )}
                </div>
            </div>
        </div>
    );
}
